import React from 'react';
import { Button, Modal } from 'equasion-lib';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import {
  unitsLabel,
  type CleanupLine,
  type PickListZone
} from '../quarantineCleanupSteps';

type CleanupPickListModalProps = {
  open: boolean;
  summary: PickListZone[];
  total: number;
  onClear: (line: CleanupLine) => void;
  onClose: () => void;
};

const click = (onClick: () => void) => ({ onClick });

/** The running pick list: everything picked so far, by zone in walk order. */
function CleanupPickListModal({
  open,
  summary,
  total,
  onClear,
  onClose
}: CleanupPickListModalProps) {
  return (
    <Modal
      className="cleanup-pick-list-modal"
      modalOpen={open}
      setModalOpen={(nextOpen?: boolean) => {
        if (!nextOpen) onClose();
      }}
    >
      <div className="cleanup-pick-list" role="dialog" aria-label="Pick List">
        <strong>Pick List</strong>
        <p>
          {total > 0
            ? `${unitsLabel(total)} picked from ${summary.length} ${
                summary.length === 1 ? 'zone' : 'zones'
              }. Nothing moves until you complete the cleanup.`
            : 'Nothing picked yet.'}
        </p>
        {summary.map(({ zone, lines, picked }) => (
          <section key={zone.id} className="cleanup-pick-list__zone">
            <h3>
              {zone.name} <small>{unitsLabel(picked)}</small>
            </h3>
            <ul>
              {lines.map(({ line, picked: linePicked }) => (
                <li key={line.zoneInventoryBalanceId}>
                  <div className="cleanup-pick-list__item">
                    <span className="cleanup-pick-list__name">{line.productName}</span>
                    <small>
                      Lot {line.lot || '—'} · Exp {formatDateOnly(line.expiration) || '—'}
                    </small>
                  </div>
                  <span className="cleanup-pick-list__qty">
                    {linePicked}
                    {linePicked < line.quantity ? ` of ${line.quantity}` : ''}
                  </span>
                  <button
                    type="button"
                    className="cleanup-pick-list__clear"
                    aria-label={`Remove ${line.productName} from the pick list`}
                    onClick={() => onClear(line)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <Button type="secondary" fullWidth {...click(onClose)}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

export default CleanupPickListModal;
